import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Download, QrCode, Printer, Copy, Check } from "lucide-react";

// Domaine PROD — le QR imprimé DOIT toujours pointer vers noldgo.com, jamais vers la preview.
const PROD_URL = "https://noldgo.com";

/**
 * Affiche un QR code téléchargeable et imprimable pour le restaurateur.
 * Le QR pointe directement vers la vitrine du resto : `noldgo.com/restaurants/{id}`.
 * Aucune liste intermédiaire — scan → menu direct → panier.
 *
 * Props :
 *  - restaurantId : UUID du restaurant (obligatoire)
 *  - restaurantName : nom affiché sous le QR (optionnel)
 */
export default function RestaurateurQRCode({ restaurantId, restaurantName }) {
  const canvasRef = useRef(null);
  const [qrPng, setQrPng] = useState("");   // PNG pur (juste le QR carré, sans habillage)
  const [posterPng, setPosterPng] = useState(""); // PNG affiche A5 imprimable
  const [copied, setCopied] = useState(false);
  const menuUrl = restaurantId ? `${PROD_URL}/restaurants/${restaurantId}` : "";

  useEffect(() => {
    if (!menuUrl) return;
    let cancelled = false;
    (async () => {
      try {
        // QR haute résolution 1200x1200 (impression jusqu'à A3)
        const qr = await QRCode.toDataURL(menuUrl, {
          width: 1200, margin: 1, errorCorrectionLevel: "H",
          color: { dark: "#1A1A1A", light: "#FFFFFF" },
        });
        if (cancelled) return;
        setQrPng(qr);

        // Génère aussi une affiche A5 (1240x1754) avec logo + slogan + QR + URL
        const canvas = canvasRef.current || document.createElement("canvas");
        const FW = 1240, FH = 1754;
        canvas.width = FW; canvas.height = FH;
        const ctx = canvas.getContext("2d");
        // Fond dégradé Nold Food (vert émeraude — couleur de la marque livraison)
        const g = ctx.createLinearGradient(0, 0, 0, FH);
        g.addColorStop(0, "#059669"); g.addColorStop(1, "#047857");
        ctx.fillStyle = g; ctx.fillRect(0, 0, FW, FH);
        // Blocs blancs
        ctx.fillStyle = "#FFFFFF";
        // Header
        ctx.font = "800 96px 'Poppins','Montserrat',system-ui,sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Nold Go", FW / 2, 180);
        ctx.font = "600 44px 'Poppins','Montserrat',system-ui,sans-serif";
        ctx.fillText("Livraison de repas à Bruxelles", FW / 2, 250);
        // Nom du resto
        if (restaurantName) {
          ctx.font = "800 68px 'Poppins','Montserrat',system-ui,sans-serif";
          const nameToDraw = restaurantName.length > 32 ? restaurantName.slice(0, 30) + "…" : restaurantName;
          ctx.fillText(nameToDraw, FW / 2, 380);
        }
        // Carte blanche avec QR
        const cardW = 900, cardH = 900, cardX = (FW - cardW) / 2, cardY = 430;
        ctx.fillStyle = "#FFFFFF";
        ctx.beginPath();
        const r = 40;
        ctx.moveTo(cardX + r, cardY);
        ctx.arcTo(cardX + cardW, cardY, cardX + cardW, cardY + cardH, r);
        ctx.arcTo(cardX + cardW, cardY + cardH, cardX, cardY + cardH, r);
        ctx.arcTo(cardX, cardY + cardH, cardX, cardY, r);
        ctx.arcTo(cardX, cardY, cardX + cardW, cardY, r);
        ctx.closePath(); ctx.fill();
        // Dessine le QR
        const qrImg = new Image();
        qrImg.onload = () => {
          ctx.drawImage(qrImg, cardX + 40, cardY + 40, cardW - 80, cardH - 80);
          // Bas de page
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "800 68px 'Poppins','Montserrat',system-ui,sans-serif";
          ctx.fillText("Scannez pour commander", FW / 2, 1470);
          ctx.font = "600 40px 'Poppins','Montserrat',system-ui,sans-serif";
          ctx.fillText("Menu direct — Zero recherche", FW / 2, 1530);
          ctx.font = "700 40px 'Poppins','Montserrat',system-ui,sans-serif";
          ctx.fillText("noldgo.com", FW / 2, 1620);
          if (!cancelled) setPosterPng(canvas.toDataURL("image/png"));
        };
        qrImg.src = qr;
      } catch (e) {
        console.error("QR generation failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [menuUrl, restaurantName]);

  const download = (dataUrl, filename) => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl; a.download = filename; a.click();
    toast.success("Téléchargement en cours ✅");
  };

  const printPoster = () => {
    if (!posterPng) return;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Autorisez les pop-ups pour imprimer."); return; }
    w.document.write(`<html><head><title>Affiche ${restaurantName || "Nold Go"}</title></head><body style="margin:0;display:flex;justify-content:center;background:#fff"><img src="${posterPng}" style="max-width:100%;height:auto"/><script>window.onload=function(){setTimeout(function(){window.print()},250)}<\/script></body></html>`);
    w.document.close();
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(menuUrl);
      setCopied(true);
      toast.success("Lien copié ✅");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Impossible de copier."); }
  };

  if (!restaurantId) {
    return (
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
        Votre vitrine sera créée automatiquement dès que vous serez publié. Le QR code apparaîtra ici.
      </div>
    );
  }

  return (
    <div data-testid="restaurateur-qrcode-card" className="rounded-2xl bg-white border border-[#E2DFD8] p-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 shrink-0 rounded-full bg-[#059669]/10 grid place-items-center">
          <QrCode className="w-5 h-5 text-[#059669]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-head font-bold text-[#1A1A1A] text-base">QR Code de votre vitrine</h3>
          <p className="text-xs text-[#75807A] mt-1">
            Vos clients scannent → arrivent <strong>directement sur votre menu</strong>.
            Aucune recherche, aucune liste — la commande démarre en 2 secondes.
          </p>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        {/* QR Code seul */}
        <div className="rounded-xl border border-[#E2DFD8] p-4">
          <div className="text-xs font-bold text-[#3B423E] uppercase tracking-wide mb-3">QR simple</div>
          {qrPng ? (
            <img src={qrPng} alt="QR Code vitrine" className="w-full aspect-square bg-white rounded-lg border border-[#E2DFD8]" data-testid="qr-image-simple" />
          ) : (
            <div className="w-full aspect-square bg-[#F0EBE1] rounded-lg animate-pulse" />
          )}
          <button
            onClick={() => download(qrPng, `qr-${(restaurantName || "vitrine").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`)}
            disabled={!qrPng}
            data-testid="qr-download-simple"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 h-10 rounded-full bg-[#1A1A1A] text-white text-sm font-semibold hover:bg-[#3B423E] disabled:opacity-50">
            <Download className="w-4 h-4" /> Télécharger PNG
          </button>
          <p className="text-[11px] text-[#75807A] mt-2 text-center">1200×1200px · impression jusqu'à A3</p>
        </div>

        {/* Affiche A5 */}
        <div className="rounded-xl border border-[#059669] bg-[#F0FDF4] p-4">
          <div className="text-xs font-bold text-[#047857] uppercase tracking-wide mb-3">Affiche A5 imprimable</div>
          {posterPng ? (
            <img src={posterPng} alt="Affiche A5 vitrine" className="w-full aspect-[1240/1754] bg-white rounded-lg border border-[#059669]/30 object-contain" data-testid="qr-image-poster" />
          ) : (
            <div className="w-full aspect-[1240/1754] bg-[#F0EBE1] rounded-lg animate-pulse" />
          )}
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              onClick={() => download(posterPng, `affiche-${(restaurantName || "vitrine").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`)}
              disabled={!posterPng}
              data-testid="qr-download-poster"
              className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full bg-[#059669] text-white text-sm font-semibold hover:bg-[#047857] disabled:opacity-50">
              <Download className="w-4 h-4" /> PNG
            </button>
            <button
              onClick={printPoster}
              disabled={!posterPng}
              data-testid="qr-print-poster"
              className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full border-2 border-[#059669] bg-white text-[#059669] text-sm font-semibold hover:bg-[#F0FDF4] disabled:opacity-50">
              <Printer className="w-4 h-4" /> Imprimer
            </button>
          </div>
        </div>
      </div>

      {/* URL cible + copier */}
      <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#F0EBE1] border border-[#E2DFD8] px-3 py-2">
        <div className="text-[11px] font-bold text-[#75807A] uppercase shrink-0">Lien</div>
        <code className="flex-1 text-xs text-[#1A1A1A] truncate font-mono" data-testid="qr-url">{menuUrl}</code>
        <button onClick={copyUrl} className="shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white border border-[#E2DFD8] text-xs font-semibold text-[#3B423E] hover:bg-[#F0EBE1]" data-testid="qr-copy-url">
          {copied ? (<><Check className="w-3.5 h-3.5 text-emerald-600" /> Copié</>) : (<><Copy className="w-3.5 h-3.5" /> Copier</>)}
        </button>
      </div>

      {/* Canvas caché utilisé pour générer l'affiche */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
